import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Background,
    BackgroundVariant,
    Controls,
    Edge,
    getNodesBounds,
    getViewportForBounds,
    Handle,
    MiniMap,
    Node,
    NodeProps,
    Position,
    ReactFlow,
    ReactFlowProvider,
    useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng, toSvg } from 'html-to-image';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Tooltip,
    Typography,
    styled,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { cyber } from '../theme';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CodeIcon from '@mui/icons-material/Code';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

type Side = 'left' | 'right';

interface ParsedNode {
    id: string;
    label: string;
    level: number;
    parentId: string | null;
    children: string[];
    branchId: string | null;
    branchLabel: string | null;
    side: Side | null;
    weight: number;
    estimatedHeight: number;
}

type MindmapNodeData = Record<string, unknown> & {
    label: string;
    accentColor: string;
    level: number;
    branchId: string | null;
    branchLabel: string | null;
    side: Side | 'center';
    relatedCount: number;
    isDimmed: boolean;
};

type MindmapEdgeData = Record<string, unknown> & {
    branchId: string | null;
    isRootEdge: boolean;
    accentColor: string;
};

interface BranchMeta {
    id: string;
    label: string;
    color: string;
    nodeCount: number;
    side: Side;
    depth: number;
}

interface ParsedMindmapResult {
    nodes: Node<MindmapNodeData>[];
    edges: Edge<MindmapEdgeData>[];
    branches: BranchMeta[];
    stats: {
        totalNodes: number;
        totalBranches: number;
        levelCount: number;
    };
}

type MindmapFlowNode = Node<MindmapNodeData, 'mindmap'>;

const BRANCH_COLORS = [cyber.primary, '#38bdf8', '#22c55e', '#f97316', '#fb7185', '#facc15', '#14b8a6'];
const ROOT_ACCENT = cyber.primaryLight;
const ROOT_BRANCH_OFFSET = 320;
const LEVEL_X_GAP = 240;
const BRANCH_Y_GAP = 56;
const CHILD_Y_GAP = 34;
const HANDLE_STYLE: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    width: 10,
    height: 10,
};

const DiagramContainer = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 24,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
    background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.94)} 0%, ${alpha(theme.palette.background.default, 0.98)} 100%)`,
    backdropFilter: 'blur(20px)',
    boxShadow: `0 24px 80px ${alpha('#000000', 0.28)}`,
}));

const DiagramCanvas = styled(Box)(({ theme }) => ({
    flex: 1,
    position: 'relative',
    minHeight: 'clamp(520px, 72vh, 860px)',
    background: `radial-gradient(circle at 18% 18%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 28%), radial-gradient(circle at 84% 14%, ${alpha(theme.palette.secondary.main, 0.14)} 0%, transparent 24%), linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.92)} 0%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`,
}));

const StatCard = styled(Box)(({ theme }) => ({
    minWidth: 88,
    padding: theme.spacing(1, 1.25),
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`,
    background: alpha(theme.palette.background.default, 0.48),
    backdropFilter: 'blur(16px)',
}));

function truncateLabel(label: string, maxLength: number): string {
    if (label.length <= maxLength) return label;
    return `${label.slice(0, maxLength - 3).trimEnd()}...`;
}

function extractMindmapLabel(rawLine: string): string {
    let label = rawLine.trim();

    label = label.replace(/^root\s*/i, '').trim();
    label = label.replace(/\s+::icon\([^)]*\)/gi, '').replace(/\s+:::[\w-]+/g, '').trim();

    let previous = '';
    while (label && label !== previous) {
        previous = label;

        const doubleParenMatch = label.match(/^\(\((.*)\)\)$/);
        if (doubleParenMatch) {
            label = doubleParenMatch[1].trim();
            continue;
        }

        const doubleBracketMatch = label.match(/^\[\[(.*)\]\]$/);
        if (doubleBracketMatch) {
            label = doubleBracketMatch[1].trim();
            continue;
        }

        const singleParenMatch = label.match(/^\((.*)\)$/);
        if (singleParenMatch) {
            label = singleParenMatch[1].trim();
            continue;
        }

        const bracketMatch = label.match(/^\[(.*)\]$/);
        if (bracketMatch) {
            label = bracketMatch[1].trim();
            continue;
        }

        const curlyMatch = label.match(/^\{\{(.*)\}\}$/);
        if (curlyMatch) {
            label = curlyMatch[1].trim();
            continue;
        }

        const cloudMatch = label.match(/^\)(.*)\($/);
        if (cloudMatch) {
            label = cloudMatch[1].trim();
        }
    }

    return label.replace(/^["'`]+|["'`]+$/g, '').trim();
}

function estimateNodeHeight(label: string, level: number): number {
    const maxCharsPerLine = level === 0 ? 28 : level === 1 ? 25 : 22;
    const wrappedLines = Math.max(1, Math.ceil(label.length / maxCharsPerLine));
    const baseHeight = level === 0 ? 92 : level === 1 ? 74 : 60;
    return baseHeight + (wrappedLines - 1) * 16;
}

function parseMermaidMindmap(mermaidCode: string): ParsedMindmapResult {
    const lines = mermaidCode
        .split('\n')
        .map((line) => line.replace(/\r/g, ''))
        .filter((line) => line.trim() && !line.trim().startsWith('%%'));

    const parsedNodes: ParsedNode[] = [];
    const nodeStack: Array<{ id: string; indent: number }> = [];
    let nodeCounter = 0;

    for (const line of lines) {
        if (line.trim().toLowerCase() === 'mindmap') continue;

        const indent = line.search(/\S/);
        if (indent === -1) continue;

        const label = extractMindmapLabel(line.trim());
        if (!label) continue;

        while (nodeStack.length > 0 && nodeStack[nodeStack.length - 1].indent >= indent) {
            nodeStack.pop();
        }

        const parentId = nodeStack.length > 0 ? nodeStack[nodeStack.length - 1].id : null;
        const level = nodeStack.length;
        const id = `node-${nodeCounter++}`;

        parsedNodes.push({
            id,
            label,
            level,
            parentId,
            children: [],
            branchId: null,
            branchLabel: null,
            side: null,
            weight: 1,
            estimatedHeight: estimateNodeHeight(label, level),
        });

        if (parentId) {
            const parent = parsedNodes.find((node) => node.id === parentId);
            if (parent) parent.children.push(id);
        }

        nodeStack.push({ id, indent });
    }

    if (parsedNodes.length === 0) {
        return {
            nodes: [],
            edges: [],
            branches: [],
            stats: { totalNodes: 0, totalBranches: 0, levelCount: 0 },
        };
    }

    const nodeMap = new Map(parsedNodes.map((node) => [node.id, node]));
    const root = parsedNodes.find((node) => node.level === 0) ?? parsedNodes[0];
    const rootChildren = root.children
        .map((childId) => nodeMap.get(childId))
        .filter((child): child is ParsedNode => Boolean(child));

    const branchColorById = new Map<string, string>();
    const branchSideById = new Map<string, Side>();
    const subtreeWeightCache = new Map<string, number>();
    const subtreeDepthCache = new Map<string, number>();
    const subtreeSpanCache = new Map<string, number>();

    const getSubtreeWeight = (nodeId: string): number => {
        const cached = subtreeWeightCache.get(nodeId);
        if (cached !== undefined) return cached;

        const node = nodeMap.get(nodeId);
        if (!node) return 0;

        const weight = 1 + node.children.reduce((sum, childId) => sum + getSubtreeWeight(childId), 0);
        subtreeWeightCache.set(nodeId, weight);
        return weight;
    };

    const getSubtreeDepth = (nodeId: string): number => {
        const cached = subtreeDepthCache.get(nodeId);
        if (cached !== undefined) return cached;

        const node = nodeMap.get(nodeId);
        if (!node) return 0;

        const depth = node.children.length === 0
            ? 1
            : 1 + Math.max(...node.children.map((childId) => getSubtreeDepth(childId)));

        subtreeDepthCache.set(nodeId, depth);
        return depth;
    };

    parsedNodes.forEach((node) => {
        node.weight = getSubtreeWeight(node.id);
    });

    if (rootChildren.length === 1) {
        branchSideById.set(rootChildren[0].id, 'right');
        branchColorById.set(rootChildren[0].id, BRANCH_COLORS[0]);
    } else {
        const sortedBranches = [...rootChildren].sort((left, right) => right.weight - left.weight);
        let leftLoad = 0;
        let rightLoad = 0;

        sortedBranches.forEach((branch, index) => {
            const preferredSide: Side = index % 2 === 0 ? 'right' : 'left';
            const side = leftLoad === rightLoad
                ? preferredSide
                : leftLoad < rightLoad
                    ? 'left'
                    : 'right';

            branchSideById.set(branch.id, side);
            branchColorById.set(branch.id, BRANCH_COLORS[index % BRANCH_COLORS.length]);

            if (side === 'left') {
                leftLoad += branch.weight;
            } else {
                rightLoad += branch.weight;
            }
        });
    }

    const assignBranchMetadata = (nodeId: string, branchId: string, branchLabel: string, side: Side) => {
        const node = nodeMap.get(nodeId);
        if (!node) return;

        node.branchId = branchId;
        node.branchLabel = branchLabel;
        node.side = side;

        node.children.forEach((childId) => assignBranchMetadata(childId, branchId, branchLabel, side));
    };

    rootChildren.forEach((branch) => {
        assignBranchMetadata(
            branch.id,
            branch.id,
            branch.label,
            branchSideById.get(branch.id) ?? 'right'
        );
    });

    const getSubtreeSpan = (nodeId: string): number => {
        const cached = subtreeSpanCache.get(nodeId);
        if (cached !== undefined) return cached;

        const node = nodeMap.get(nodeId);
        if (!node) return 0;

        if (node.children.length === 0) {
            subtreeSpanCache.set(nodeId, node.estimatedHeight);
            return node.estimatedHeight;
        }

        const childSpan = node.children.reduce((sum, childId) => sum + getSubtreeSpan(childId), 0)
            + CHILD_Y_GAP * Math.max(0, node.children.length - 1);

        const span = Math.max(node.estimatedHeight, childSpan);
        subtreeSpanCache.set(nodeId, span);
        return span;
    };

    const positions = new Map<string, { x: number; y: number }>();
    positions.set(root.id, { x: 0, y: 0 });

    const layoutBranchTree = (nodeId: string, x: number, centerY: number, side: Side) => {
        positions.set(nodeId, { x, y: centerY });

        const node = nodeMap.get(nodeId);
        if (!node || node.children.length === 0) return;

        const children = node.children
            .map((childId) => nodeMap.get(childId))
            .filter((child): child is ParsedNode => Boolean(child));

        const totalChildrenSpan = children.reduce((sum, child) => sum + getSubtreeSpan(child.id), 0)
            + CHILD_Y_GAP * Math.max(0, children.length - 1);

        let cursor = centerY - totalChildrenSpan / 2;

        children.forEach((child) => {
            const childSpan = getSubtreeSpan(child.id);
            const childCenterY = cursor + childSpan / 2;
            const childX = x + (side === 'right' ? LEVEL_X_GAP : -LEVEL_X_GAP);

            layoutBranchTree(child.id, childX, childCenterY, side);
            cursor += childSpan + CHILD_Y_GAP;
        });
    };

    const layoutSide = (side: Side) => {
        const sideBranches = rootChildren.filter((branch) => (branchSideById.get(branch.id) ?? 'right') === side);
        if (sideBranches.length === 0) return;

        const totalSpan = sideBranches.reduce((sum, branch) => sum + getSubtreeSpan(branch.id), 0)
            + BRANCH_Y_GAP * Math.max(0, sideBranches.length - 1);

        let cursor = -totalSpan / 2;

        sideBranches.forEach((branch) => {
            const branchSpan = getSubtreeSpan(branch.id);
            const branchCenterY = cursor + branchSpan / 2;
            const branchX = side === 'right' ? ROOT_BRANCH_OFFSET : -ROOT_BRANCH_OFFSET;

            layoutBranchTree(branch.id, branchX, branchCenterY, side);
            cursor += branchSpan + BRANCH_Y_GAP;
        });
    };

    layoutSide('left');
    layoutSide('right');

    const branches: BranchMeta[] = rootChildren.map((branch) => ({
        id: branch.id,
        label: branch.label,
        color: branchColorById.get(branch.id) ?? BRANCH_COLORS[0],
        nodeCount: getSubtreeWeight(branch.id),
        side: branchSideById.get(branch.id) ?? 'right',
        depth: getSubtreeDepth(branch.id),
    }));

    const nodes: Node<MindmapNodeData>[] = parsedNodes.map((node) => ({
        id: node.id,
        type: 'mindmap',
        position: positions.get(node.id) ?? { x: 0, y: 0 },
        draggable: false,
        selectable: false,
        connectable: false,
        data: {
            label: node.label,
            accentColor: node.level === 0 ? ROOT_ACCENT : branchColorById.get(node.branchId ?? node.id) ?? BRANCH_COLORS[0],
            level: node.level,
            branchId: node.branchId,
            branchLabel: node.branchLabel,
            side: node.level === 0 ? 'center' : node.side ?? 'right',
            relatedCount: node.level === 1 ? Math.max(node.weight - 1, 0) : node.children.length,
            isDimmed: false,
        },
    }));

    const edges: Edge<MindmapEdgeData>[] = parsedNodes
        .filter((node) => node.parentId)
        .map((node) => {
            const branchColor = branchColorById.get(node.branchId ?? node.id) ?? BRANCH_COLORS[0];
            const side = node.side ?? 'right';
            const isRootEdge = node.parentId === root.id;

            return {
                id: `edge-${node.parentId}-${node.id}`,
                source: node.parentId!,
                target: node.id,
                sourceHandle: side === 'left' ? 'left' : 'right',
                targetHandle: side === 'left' ? 'right' : 'left',
                type: 'smoothstep',
                selectable: false,
                data: {
                    branchId: node.branchId,
                    isRootEdge,
                    accentColor: branchColor,
                },
                style: {
                    stroke: branchColor,
                    strokeWidth: isRootEdge ? 2.8 : 1.9,
                    opacity: 0.82,
                },
            };
        });

    return {
        nodes,
        edges,
        branches,
        stats: {
            totalNodes: parsedNodes.length,
            totalBranches: branches.length,
            levelCount: Math.max(...parsedNodes.map((node) => node.level)) + 1,
        },
    };
}

const MindmapNode = ({ data }: NodeProps<MindmapFlowNode>) => {
    const theme = useTheme();
    const isRoot = data.level === 0;
    const isPrimaryBranch = data.level === 1;
    const secondaryLabel = isRoot
        ? 'Central report narrative'
        : isPrimaryBranch
            ? `${data.relatedCount} linked nodes`
            : data.relatedCount > 0
                ? `${data.relatedCount} child nodes`
                : undefined;

    return (
        <>
            <Handle type="source" position={Position.Left} id="left" style={HANDLE_STYLE} />
            <Handle type="target" position={Position.Left} id="left" style={HANDLE_STYLE} />
            <Handle type="source" position={Position.Right} id="right" style={HANDLE_STYLE} />
            <Handle type="target" position={Position.Right} id="right" style={HANDLE_STYLE} />
            <Box
                sx={{
                    position: 'relative',
                    maxWidth: isRoot ? 320 : isPrimaryBranch ? 268 : 228,
                    minWidth: isRoot ? 220 : isPrimaryBranch ? 180 : 146,
                    px: isRoot ? 3 : isPrimaryBranch ? 2.25 : 1.75,
                    py: isRoot ? 2.5 : isPrimaryBranch ? 1.8 : 1.45,
                    borderRadius: isRoot ? '22px' : '18px',
                    border: `1px solid ${alpha(data.accentColor, isRoot ? 0.58 : 0.42)}`,
                    background: isRoot
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.72)} 0%, ${alpha(theme.palette.primary.light, 0.88)} 100%)`
                        : `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 0.94)} 100%)`,
                    boxShadow: data.isDimmed
                        ? `0 12px 30px ${alpha('#000000', 0.16)}`
                        : `0 22px 60px ${alpha(data.accentColor, isRoot ? 0.24 : 0.12)}`,
                    backdropFilter: 'blur(16px)',
                    opacity: data.isDimmed ? 0.34 : 1,
                    transform: data.isDimmed ? 'scale(0.98)' : 'scale(1)',
                    transition: 'transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease, border-color 180ms ease',
                    cursor: 'pointer',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 'inherit',
                        background: `linear-gradient(135deg, ${alpha(data.accentColor, isRoot ? 0.16 : 0.09)} 0%, transparent 58%)`,
                        pointerEvents: 'none',
                    },
                    '&:hover': {
                        transform: data.isDimmed ? 'scale(0.99)' : 'translateY(-2px)',
                        boxShadow: `0 24px 64px ${alpha(data.accentColor, 0.2)}`,
                    },
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'grid', gap: 0.5 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            fontWeight: 700,
                            color: isRoot ? alpha(theme.palette.common.white, 0.78) : alpha(data.accentColor, 0.9),
                        }}
                    >
                        {isRoot ? 'Threat overview' : isPrimaryBranch ? 'Primary branch' : `Level ${data.level}`}
                    </Typography>
                    <Typography
                        sx={{
                            color: isRoot ? theme.palette.common.white : theme.palette.text.primary,
                            fontSize: isRoot ? '1rem' : isPrimaryBranch ? '0.94rem' : '0.84rem',
                            fontWeight: isRoot ? 700 : isPrimaryBranch ? 600 : 500,
                            lineHeight: 1.35,
                            wordBreak: 'break-word',
                        }}
                    >
                        {data.label}
                    </Typography>
                    {secondaryLabel ? (
                        <Typography
                            variant="caption"
                            sx={{
                                color: isRoot ? alpha(theme.palette.common.white, 0.78) : theme.palette.text.secondary,
                                lineHeight: 1.3,
                            }}
                        >
                            {secondaryLabel}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
        </>
    );
};

const nodeTypes = {
    mindmap: MindmapNode,
};

const MindmapStats = ({
    label,
    value,
}: {
    label: string;
    value: string | number;
}) => {
    const theme = useTheme();

    return (
        <StatCard>
            <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: cyber.fontMono, color: alpha(cyber.accent.cyan, 0.9) }}>
                {value}
            </Typography>
        </StatCard>
    );
};

const MindmapFlow: React.FC<{
    chart: string;
    isFullscreen: boolean;
    onToggleFullscreen: () => void;
}> = ({ chart, isFullscreen, onToggleFullscreen }) => {
    const theme = useTheme();
    const showMiniMap = useMediaQuery(theme.breakpoints.up('lg'));
    const flowWrapperRef = useRef<HTMLDivElement | null>(null);
    const { fitView, getNodes } = useReactFlow();
    const { nodes: parsedNodes, edges: parsedEdges, branches, stats } = useMemo(() => parseMermaidMindmap(chart), [chart]);
    const [activeBranchId, setActiveBranchId] = useState<string | null>(null);
    const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);

    React.useEffect(() => {
        setActiveBranchId(null);
    }, [chart]);

    const getFilteredMeasuredNodes = useCallback((branchId: string | null) => {
        const measuredNodes = getNodes();
        return branchId
            ? measuredNodes.filter((node) => node.data?.level === 0 || node.data?.branchId === branchId)
            : measuredNodes;
    }, [getNodes]);

    const fitGraphToViewport = useCallback((branchId: string | null, attempt = 0) => {
        const nodesForFit = getFilteredMeasuredNodes(branchId);
        const nodesReady = nodesForFit.length > 0 && nodesForFit.every((node) => {
            const measuredWidth = node.measured?.width ?? node.width ?? 0;
            const measuredHeight = node.measured?.height ?? node.height ?? 0;
            return measuredWidth > 0 && measuredHeight > 0;
        });

        if (!nodesReady) {
            if (attempt < 8) {
                window.setTimeout(() => fitGraphToViewport(branchId, attempt + 1), 90);
            }
            return;
        }

        void fitView({
            nodes: nodesForFit,
            padding: branchId ? 0.18 : 0.1,
            duration: 500,
            minZoom: 0.2,
            maxZoom: branchId ? 1.22 : 1.08,
        });
    }, [fitView, getFilteredMeasuredNodes]);

    const displayNodes = useMemo(
        () => parsedNodes.map((node) => ({
            ...node,
            data: {
                ...node.data,
                isDimmed: Boolean(activeBranchId && node.data.level !== 0 && node.data.branchId !== activeBranchId),
            },
        })),
        [parsedNodes, activeBranchId]
    );

    const displayEdges = useMemo(
        () => parsedEdges.map((edge) => {
            const isActive = !activeBranchId || edge.data?.branchId === activeBranchId;
            return {
                ...edge,
                style: {
                    ...edge.style,
                    stroke: edge.data?.accentColor ?? edge.style?.stroke,
                    opacity: isActive ? 0.88 : 0.14,
                    strokeWidth: isActive
                        ? edge.data?.isRootEdge
                            ? 2.8
                            : 2.05
                        : 1.15,
                },
            };
        }),
        [parsedEdges, activeBranchId]
    );

    React.useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            fitGraphToViewport(activeBranchId);
        }, isFullscreen ? 140 : 40);

        return () => window.clearTimeout(timeoutId);
    }, [activeBranchId, displayNodes, fitGraphToViewport, isFullscreen]);

    const handleBranchFocus = useCallback((branchId: string | null) => {
        setActiveBranchId(branchId);
    }, []);

    const handleDownloadClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setDownloadAnchor(event.currentTarget);
    }, []);

    const handleDownloadClose = useCallback(() => {
        setDownloadAnchor(null);
    }, []);

    const exportCurrentView = useCallback(async (format: 'png' | 'svg') => {
        const viewportElement = flowWrapperRef.current?.querySelector('.react-flow__viewport') as HTMLElement | null;
        if (!viewportElement) return;

        const nodesForExport = getFilteredMeasuredNodes(activeBranchId);

        if (nodesForExport.length === 0) return;

        try {
            const bounds = getNodesBounds(nodesForExport);
            const padding = 64;
            const width = Math.max(Math.ceil(bounds.width + padding * 2), 320);
            const height = Math.max(Math.ceil(bounds.height + padding * 2), 240);
            const viewport = getViewportForBounds(bounds, width, height, 0.1, 1.8, 0.08);
            const exportOptions = {
                backgroundColor: theme.palette.background.default,
                width,
                height,
                pixelRatio: 2,
                cacheBust: true,
                style: {
                    width: `${width}px`,
                    height: `${height}px`,
                    transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
                    transformOrigin: '0 0',
                },
            };

            const dataUrl = format === 'png'
                ? await toPng(viewportElement, exportOptions)
                : await toSvg(viewportElement, exportOptions);

            const link = document.createElement('a');
            link.download = `mindmap-${Date.now()}.${format}`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            console.error(`Error exporting ${format.toUpperCase()}:`, error);
        } finally {
            handleDownloadClose();
        }
    }, [activeBranchId, getFilteredMeasuredNodes, handleDownloadClose, theme.palette.background.default]);

    return (
        <>
            <Box
                sx={{
                    p: isFullscreen ? { xs: 1.5, md: 2 } : { xs: 2, md: 2.5 },
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
                    backdropFilter: 'blur(16px)',
                }}
            >
                <Stack direction={{ xs: 'column', xl: 'row' }} justifyContent="space-between" gap={2}>
                    <Stack spacing={1.25} sx={{ maxWidth: 820 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Interactive Threat Mindmap
                        </Typography>
                        {!isFullscreen ? (
                            <>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                                    Branches are color-coded by the first analytical domain. Focus a branch to isolate infrastructure, capabilities and delivery patterns, or export the current view for sharing.
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                    <MindmapStats label="Nodes" value={stats.totalNodes} />
                                    <MindmapStats label="Branches" value={stats.totalBranches} />
                                    <MindmapStats label="Levels" value={stats.levelCount} />
                                </Stack>
                            </>
                        ) : (
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                Fullscreen mode keeps the graph centered and interactive.
                            </Typography>
                        )}
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <Button
                            variant="outlined"
                            startIcon={<CenterFocusStrongIcon />}
                            onClick={() => handleBranchFocus(null)}
                            sx={{
                                borderColor: alpha(theme.palette.primary.main, 0.25),
                                color: theme.palette.text.primary,
                                backgroundColor: alpha(theme.palette.background.default, 0.28),
                            }}
                        >
                            Reset view
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={isFullscreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                            onClick={onToggleFullscreen}
                            sx={{
                                borderColor: alpha(theme.palette.primary.main, 0.25),
                                color: theme.palette.text.primary,
                                backgroundColor: alpha(theme.palette.background.default, 0.28),
                            }}
                        >
                            {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadClick}
                            sx={{
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.28)}`,
                            }}
                        >
                            Export
                        </Button>
                    </Stack>
                </Stack>
                {branches.length > 0 ? (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                            label="All branches"
                            clickable
                            onClick={() => handleBranchFocus(null)}
                            variant={activeBranchId === null ? 'filled' : 'outlined'}
                            sx={{
                                borderColor: alpha(theme.palette.primary.main, 0.22),
                                backgroundColor: activeBranchId === null
                                    ? alpha(theme.palette.primary.main, 0.18)
                                    : alpha(theme.palette.background.default, 0.34),
                            }}
                        />
                        {branches.map((branch) => (
                            <Tooltip
                                key={branch.id}
                                title={`${branch.label} | ${branch.nodeCount} nodes | ${branch.depth} levels`}
                                arrow
                            >
                                <Chip
                                    label={`${truncateLabel(branch.label, 34)} | ${branch.nodeCount}`}
                                    clickable
                                    onClick={() => handleBranchFocus(branch.id)}
                                    variant={activeBranchId === branch.id ? 'filled' : 'outlined'}
                                    sx={{
                                        borderColor: alpha(branch.color, 0.44),
                                        color: activeBranchId === branch.id ? theme.palette.text.primary : theme.palette.text.secondary,
                                        backgroundColor: activeBranchId === branch.id
                                            ? alpha(branch.color, 0.18)
                                            : alpha(theme.palette.background.default, 0.34),
                                        '& .MuiChip-label': {
                                            fontWeight: activeBranchId === branch.id ? 700 : 500,
                                        },
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                ) : null}
            </Box>

            <DiagramCanvas ref={flowWrapperRef}>
                <ReactFlow
                    nodes={displayNodes}
                    edges={displayEdges}
                    nodeTypes={nodeTypes}
                    onNodeClick={(_event, node) => handleBranchFocus(node.data.level === 0 ? null : node.data.branchId)}
                    fitView
                    fitViewOptions={{
                        padding: 0.1,
                        minZoom: 0.2,
                        maxZoom: 1.08,
                    }}
                    nodeOrigin={[0.5, 0.5]}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    minZoom={0.18}
                    maxZoom={1.6}
                    proOptions={{ hideAttribution: true }}
                    style={{ background: 'transparent' }}
                >
                    <Background
                        variant={BackgroundVariant.Dots}
                        gap={22}
                        size={1.2}
                        color={alpha(theme.palette.common.white, 0.08)}
                    />
                    <Controls
                        showInteractive={false}
                        position="bottom-right"
                        style={{
                            background: alpha(theme.palette.background.paper, 0.74),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                            borderRadius: 14,
                            boxShadow: `0 10px 24px ${alpha('#000000', 0.2)}`,
                        }}
                    />
                    {showMiniMap ? (
                        <MiniMap
                            position="bottom-left"
                            nodeColor={(node) => (node.data?.accentColor as string) || theme.palette.primary.main}
                            maskColor={alpha(theme.palette.background.default, 0.76)}
                            style={{
                                background: alpha(theme.palette.background.paper, 0.72),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                                borderRadius: 16,
                                boxShadow: `0 12px 28px ${alpha('#000000', 0.22)}`,
                            }}
                        />
                    ) : null}
                </ReactFlow>
            </DiagramCanvas>

            <Menu
                anchorEl={downloadAnchor}
                open={Boolean(downloadAnchor)}
                onClose={handleDownloadClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                            background: alpha(theme.palette.background.paper, 0.96),
                            backdropFilter: 'blur(20px)',
                        },
                    },
                }}
            >
                <MenuItem onClick={() => void exportCurrentView('png')}>
                    <ListItemIcon>
                        <ImageIcon fontSize="small" sx={{ color: cyber.accent.amber }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Download PNG"
                        secondary="Presentation-ready image"
                        secondaryTypographyProps={{ variant: 'caption', sx: { color: theme.palette.text.secondary } }}
                    />
                </MenuItem>
                <MenuItem onClick={() => void exportCurrentView('svg')}>
                    <ListItemIcon>
                        <CodeIcon fontSize="small" sx={{ color: cyber.accent.cyan }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Download SVG"
                        secondary="Scalable vector export"
                        secondaryTypographyProps={{ variant: 'caption', sx: { color: theme.palette.text.secondary } }}
                    />
                </MenuItem>
            </Menu>
        </>
    );
};

interface MermaidDiagramProps {
    chart: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    React.useEffect(() => {
        setIsLoading(!chart);
    }, [chart]);

    React.useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const handleToggleFullscreen = useCallback(async () => {
        const container = containerRef.current;
        if (!container) return;

        try {
            if (document.fullscreenElement === container) {
                await document.exitFullscreen();
                return;
            }

            await container.requestFullscreen();
        } catch (error) {
            console.error('Error toggling fullscreen:', error);
        }
    }, []);

    if (isLoading || !chart) {
        return (
            <DiagramContainer elevation={0}>
                <Box
                    sx={{
                        minHeight: 'clamp(420px, 58vh, 640px)',
                        display: 'grid',
                        placeItems: 'center',
                        gap: 1.5,
                        background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.96)} 100%)`,
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={46} sx={{ color: theme.palette.primary.main, mb: 2 }} />
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                            Loading mindmap...
                        </Typography>
                    </Box>
                </Box>
            </DiagramContainer>
        );
    }

    return (
        <DiagramContainer
            ref={containerRef}
            elevation={0}
            sx={isFullscreen ? {
                width: '100vw',
                height: '100dvh',
                borderRadius: 0,
                border: 'none',
                boxShadow: 'none',
            } : undefined}
        >
            <ReactFlowProvider>
                <MindmapFlow
                    chart={chart}
                    isFullscreen={isFullscreen}
                    onToggleFullscreen={handleToggleFullscreen}
                />
            </ReactFlowProvider>
        </DiagramContainer>
    );
};

export default MermaidDiagram;